import axios from 'axios';
import environment from '../config/environment';

class MongoDBAnalyticsService {
  constructor() {
    this.env = environment();
    
    // Set the base URL for API calls - use relative URL to avoid CORS issues
    this.baseUrl = '/api';
    
    // Analytics endpoints
    this.endpoints = {
      metrics: `${this.baseUrl}/analytics/mongodb/metrics`,
      health: `${this.baseUrl}/analytics/mongodb/health`,
      performance: `${this.baseUrl}/analytics/mongodb/performance`,
      connection: `${this.baseUrl}/analytics/mongodb/connection`
    };
    
    // Connection state
    this.connectionState = {
      status: 'disconnected',
      lastChecked: null,
      error: null
    };
    
    // Debug mode
    this.debug = true;
  }

  log(...args) {
    if (this.debug) {
      console.log('[MongoDBAnalyticsService]', ...args);
    }
  }

  logError(...args) {
    console.error('[MongoDBAnalyticsService]', ...args);
  }

  async getMongoDBAnalytics() {
    try {
      this.log('Fetching MongoDB analytics data...');
      
      // First check connection
      const connectionStatus = await this.checkConnection();
      this.log('Connection status:', connectionStatus);
      
      if (connectionStatus.status !== 'connected') {
        return {
          connectionState: connectionStatus,
          totalClusters: 0,
          activeClusters: 0,
          clusters: [],
          timestamp: new Date().toISOString()
        };
      }
      
      // Get all metrics in parallel
      const [metrics, health, performance] = await Promise.all([
        this.getClusterMetrics(),
        this.getClusterHealth(),
        this.getPerformanceMetrics()
      ]);

      this.log('Metrics data received');
      
      // Process the data
      const clusters = this.processClusterData({ metrics, health, performance });
      
      return {
        connectionState: connectionStatus,
        totalClusters: clusters.length,
        activeClusters: clusters.filter(c => c.status === 'RUNNING').length,
        clusters,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.logError('Error fetching MongoDB analytics:', error);
      return {
        connectionState: {
          status: 'error',
          error: error.message,
          lastChecked: new Date()
        },
        clusters: [],
        totalClusters: 0,
        activeClusters: 0
      };
    }
  }

  async checkConnection() {
    try {
      this.log('Checking MongoDB connection...');
      const response = await axios.get(this.endpoints.connection);
      
      this.log('Connection response:', response.data);
      
      this.connectionState = {
        status: response.data.status,
        lastChecked: new Date(),
        error: response.data.error || null
      };
      return this.connectionState;
    } catch (error) {
      this.logError('Error checking MongoDB connection:', error);
      this.connectionState = {
        status: 'error',
        lastChecked: new Date(),
        error: error.message
      };
      return this.connectionState;
    }
  }

  async getClusterMetrics() {
    try {
      const response = await axios.get(this.endpoints.metrics);
      return response.data;
    } catch (error) {
      this.logError('Error fetching cluster metrics:', error);
      return {
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  async getClusterHealth() {
    try {
      const response = await axios.get(this.endpoints.health);
      return response.data;
    } catch (error) {
      this.logError('Error fetching cluster health:', error);
      return {
        status: 'ERROR',
        error: error.message
      };
    }
  }

  async getPerformanceMetrics() {
    try {
      const response = await axios.get(this.endpoints.performance);
      return response.data;
    } catch (error) {
      this.logError('Error fetching performance metrics:', error);
      return {
        error: error.message
      };
    }
  }

  async getCollectionStats(collectionName) {
    try {
      this.log(`Fetching stats for collection: ${collectionName}`);
      const response = await axios.get(`${this.baseUrl}/analytics/mongodb/collection/${collectionName}/stats`);
      return response.data;
    } catch (error) {
      this.logError(`Error fetching collection stats for ${collectionName}:`, error);
      return {
        error: error.message,
        name: collectionName,
        count: 0,
        size: 0,
        avgObjSize: 0,
        storageSize: 0,
        nindexes: 0,
        totalIndexSize: 0
      };
    }
  }

  processClusterData({ metrics, health, performance }) {
    try {
      if (!metrics || !metrics.databases || metrics.databases.length === 0) {
        return [];
      }

      return metrics.databases.map(db => {
        return {
          name: db.name,
          status: health?.status || 'UNKNOWN',
          host: health?.host || 'unknown',
          mongoDBVersion: metrics.version || 'Unknown',
          collectionCount: db.collections?.length || 0,
          documentCount: metrics.documentCount || 0,
          storageSize: db.storageSize || 0,
          diskSizeGB: null, // Not available in the metrics
          collections: db.collections || [],
          connectionStrings: {
            standard: health?.host || 'mongodb://localhost:27017'
          },
          performance: performance || {}
        };
      });
    } catch (error) {
      this.logError('Error processing cluster data:', error);
      return [];
    }
  }

  formatBytes(bytes, decimals = 2) {
    if (bytes === 0 || !bytes) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
  }
}

// Create and export singleton instance
const mongoDBAnalyticsService = new MongoDBAnalyticsService();
export default mongoDBAnalyticsService; 